<script setup lang="ts">
// ================================
// L'encadré « Comparaison avec votre dernière étude » (D29 du 05/10/2026,
// maquette 1 amendée par le vote : un encadré DANS l'étude, pas au premier
// plan). Lecture seule : la dernière étude de l'ancienne version du compteur,
// ce que la nouvelle trouve à la place, les seules raisons de l'écart qui
// jouent sur ce compteur et le lien vers l'étude d'origine.
//
// Revue 2 en production (15/10/2026, I16, I32, I38) : deux lignes et un lien.
// Ligne 1, les deux montants ; ligne 2, les deux périodes quand elles
// diffèrent ; puis les autres raisons de l'écart, une ligne chacune, et le
// lien, avec la date de fin de l'ancienne version en métadonnée.
//
// Le composant ne décide rien : la comparaison lui arrive toute lue
// (`lireComparaisonV1`). Sans comparaison, il ne rend rien.
// ================================
import { computed, ref } from "vue"
import OptimTurpeV2TexteSigne from "./OptimTurpeV2TexteSigne.vue"
import { FIN_DE_VIE_V1 } from "@modules/courtage/constants/optim-turpe-v2"
import {
    ligneDesMontants,
    lignePeriodes,
} from "@modules/courtage/utils/optim-turpe-v2.pont"
import type { ComparaisonV1 } from "@modules/courtage/utils/optim-turpe-v2.pont"

type Props = {
    comparaison: ComparaisonV1 | null
    /** Le bâtiment et le compteur, pour le lien vers l'ancienne version. */
    buildingId: string
    sourceId: string
}
const props = defineProps<Props>()
const emit = defineEmits<{ choisir: [] }>()

const getLienV1 = computed(() => ({
    name: "PageBuildingOptimisationContractuelle",
    params: { _id_building: props.buildingId },
    query: props.sourceId ? { idsource: props.sourceId } : {},
}))

/** Ligne 1 : les deux montants (`ligneDesMontants`). */
const getLigneMontants = computed<string>(() =>
    props.comparaison ? ligneDesMontants(props.comparaison) : ""
)

/** Ligne 2 : les deux périodes servies, quand elles diffèrent. */
const getLignePeriodes = computed<string | null>(() =>
    props.comparaison ? lignePeriodes(props.comparaison) : null
)

/** Les autres raisons de l'écart (dépassements, planchers, raccordement). */
const getAutresRaisons = computed(
    () => props.comparaison?.raisons.filter((r) => r.code !== "periode") ?? []
)

/** Les raisons de l'écart, repliées derrière un bouton texte (revue 2). */
const raisonsOuvertes = ref(false)

/** « 31/12/2026 » : la fin de l'ancienne version, en métadonnée du lien. */
const getFinDeVie = computed(() => {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(FIN_DE_VIE_V1)
    return m ? `${m[3]}/${m[2]}/${m[1]}` : FIN_DE_VIE_V1
})
</script>

<template>
    <div
        v-if="comparaison"
        class="flex flex-col gap-2 text-sm text-surface-700"
        data-testid="optim-turpe-v2-comparaison"
    >
        <p
            class="text-primary-900"
            data-testid="optim-turpe-v2-comparaison-ancienne"
        >
            <OptimTurpeV2TexteSigne :texte="getLigneMontants" />
        </p>
        <p
            v-if="getLignePeriodes"
            data-testid="optim-turpe-v2-comparaison-periodes"
        >
            {{ getLignePeriodes }}
        </p>

        <BaseButton
            v-if="getAutresRaisons.length > 0"
            :label="
                raisonsOuvertes
                    ? 'Masquer pourquoi les montants diffèrent'
                    : 'Pourquoi les montants diffèrent'
            "
            size="small"
            text
            class="w-fit p-0!"
            :aria-expanded="raisonsOuvertes"
            data-testid="optim-turpe-v2-comparaison-pourquoi"
            @click="raisonsOuvertes = !raisonsOuvertes"
        />
        <p
            v-for="raison in raisonsOuvertes ? getAutresRaisons : []"
            :key="raison.code"
            :data-testid="`optim-turpe-v2-comparaison-raison-${raison.code}`"
        >
            <span class="font-medium text-primary-900"
                >{{ raison.titre }} : </span
            >{{ raison.phrase }}
            <button
                v-if="raison.comparer"
                type="button"
                class="font-medium text-primary-800 underline underline-offset-2"
                data-testid="optim-turpe-v2-comparaison-comparer"
                @click="emit('choisir')"
            >
                Comparer les deux scénarios
            </button>
        </p>

        <p class="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <router-link
                :to="getLienV1"
                class="font-medium text-primary-700 underline"
                data-testid="optim-turpe-v2-comparaison-lien-v1"
                >Voir l'étude de l'ancienne version</router-link
            >
            <span
                class="text-xs text-surface-500"
                data-testid="optim-turpe-v2-comparaison-fin"
                >reste consultable au moins jusqu'au {{ getFinDeVie }}</span
            >
        </p>
    </div>
</template>
